/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { defineMessages, injectIntl } from 'react-intl';

import { Input, Form } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import BaseCalendar from '../../containers/form/Calendar';

import { ClientSelect } from '../../containers/form';

import CabinetServiceFieldList from '../../containers/form/CabinetServiceFieldList';
import MasterServiceFieldList from '../../containers/form/MasterServiceFieldList';

import styles from '../../../../styles';

import type { Intl } from '../../../../type';

const intlMessages = {
    client: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.clientPlaceholder',
            defaultMessage: 'Select a client',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.clientLabel',
            defaultMessage: 'Client',
        },
        walkin: {
            id: 'pages.appointments.details.AppointmentDetails.walkinClient',
            defaultMessage: 'Walk-in Client',
        },
    }),

    date: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.datePlaceholder',
            defaultMessage: 'Select an appointment date',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.dateLabel',
            defaultMessage: 'Date',
        },
    }),

    notes: defineMessages({
        placeholder: {
            id: 'pages.schedule.form.AppointmentForm.notesPlaceholder',
            defaultMessage: 'Write a note (optional)',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.notesLabel',
            defaultMessage: 'Notes',
        },
    }),
};

type CreateAppointmentFormProps = {
    onCloseDialog: Function,
    defaultMaster: string,
    defaultCabinet: string,
    inCabinet: boolean,
    salon: Object,
    intl: Intl,
};

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Calendar = styled(BaseCalendar)`
    & .DayPickerInput-OverlayWrapper {
        top: 46px;
    }
`;

function CreateAppointmentForm({
    onCloseDialog,
    defaultMaster,
    defaultCabinet,
    inCabinet,
    intl,
}: CreateAppointmentFormProps) {
    const { control, setValue } = useFormContext();

    return (
        <Form>
            <Form.Group>
                <Controller
                    name="client"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <Form.Field width={12} error={!!fieldState.error}>
                            <Label>
                                {intl.formatMessage(intlMessages.client.label)}
                            </Label>
                            <ClientSelect
                                value={value}
                                onChange={(e, { value: v }) => onChange(v)}
                                name="client"
                                change={setValue}
                                placeholder={intl.formatMessage(intlMessages.client.walkin)}
                                search
                                onCloseDialog={onCloseDialog}
                                newAppointmentUseCase={true}
                            />
                        </Form.Field>
                    )}
                />
                <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <Form.Field width={4} required error={!!fieldState.error}>
                            <Label>{intl.formatMessage(intlMessages.date.label)}</Label>
                            <Calendar
                                value={value}
                                onChange={onChange}
                                format="dd.MM.yyyy"
                                placeholder={intl.formatMessage(intlMessages.date.placeholder)}
                                dayPickerProps={{
                                    numberOfMonths: 2,
                                    fixedWeeks: true,
                                }}
                            />
                        </Form.Field>
                    )}
                />
            </Form.Group>
            <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange, onBlur, name, ref } }) => (
                    <Form.Field>
                        <Label>{intl.formatMessage(intlMessages.notes.label)}</Label>
                        <Input
                            value={value || ''}
                            onChange={e => onChange(e.target.value)}
                            onBlur={onBlur}
                            name={name}
                            ref={ref}
                            placeholder={intl.formatMessage(intlMessages.notes.placeholder)}
                            fluid
                        />
                    </Form.Field>
                )}
            />
            {inCabinet ? (
                <CabinetServiceFieldList
                    defaultCabinet={defaultCabinet}
                    withLabel
                    multiple
                />
            ) : (
                <MasterServiceFieldList
                    defaultMaster={defaultMaster}
                    withLabel
                    multiple
                />
            )}
        </Form>
    );
}

export default injectIntl(CreateAppointmentForm);
